import TextArea from "../../../../Components/Common/TextArea/TextArea";
import Input from "../../../../Components/Common/Input/Input";
import { CHAR_LIMIT } from "../../../../utils/Common/Constant";
import { useRef, useState } from "react";
import { SovItem, StatusOption } from "../types/inspector.types";
import Button from "Components/Common/Button/Button";

interface SovItemSectionProps {
  item: SovItem;
  collapsed: boolean;
  statusOptions: StatusOption[];
  onToggle: () => void;
  onSetStatus: (status: number) => void;
  onSetCustomPct: (value: string) => void;
  onClearCustomPct: () => void;
  onToggleFlag: () => void;
  onFlagWithComment: (text: string) => void;
  onAddComment: (text: string) => void;
}

export default function SovItemSection({
  item,
  collapsed,
  statusOptions,
  onToggle,
  onSetStatus,
  onSetCustomPct,
  onClearCustomPct,
  onToggleFlag,
  onFlagWithComment,
  onAddComment,
}: SovItemSectionProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [pendingFlag, setPendingFlag] = useState(false);
  const [draft, setDraft] = useState("");

  const activeStatus =
    item.customPct === "" ? item.status : Number(item.customPct);
  const warnClass =
    item.warningLevel === "amber" ? "warnBoxAmber" : "warnBoxRed";
  const flagComment = item.flagComment ?? "";
  const extraComments = item.extraComments ?? [];

  const handleFlagClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.flagged) {
      onToggleFlag();
      return;
    }
    setPendingFlag(true);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    if (pendingFlag) {
      onFlagWithComment(text);
      setPendingFlag(false);
    } else {
      onAddComment(text);
    }
    setDraft("");
  };

  const handleBlur = () => {
    if (pendingFlag && !draft.trim()) {
      setPendingFlag(false);
    }
  };

  const hasComments = flagComment || extraComments.length > 0;

  return (
    <div className={`sovItemWrap${item.flagged ? " flagged" : ""}`}>
      <div
        className={`sovSectionHeader${item.flagged ? " flagged" : ""}${collapsed ? " collapsed" : ""}`}
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        <div className="sshLeft">
          <span
            className={`sshCode${item.critical ? " critical" : item.flagged ? " flagged" : ""}`}
          >
            {item.code}
          </span>
          <div>
            <div
              className={`sshDesc${item.critical ? " critical" : item.flagged ? " flagged" : ""}`}
            >
              {item.desc}
            </div>
            <div
              className={`sshSub${item.critical ? " critical" : item.flagged ? " flagged" : ""}`}
            >
              {item.sub}
              {item.flagged && flagComment && (
                <>
                  {" "}
                  · ⚠{" "}
                  {flagComment.length > 35
                    ? `${flagComment.slice(0, 35)}…`
                    : flagComment}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="sshRight">
          <span className={`sshAmount${item.flagged ? " flagged" : ""}`}>
            {item.amountBilled}
            {item.flagged ? " ⚠" : ""}
          </span>
          <span className="sshChevron">▼</span>
        </div>
      </div>

      <div className={`sovSectionBody${collapsed ? " collapsed" : ""}`}>
        <div className="fiItem">
          <div className={`billingRow${item.flagged ? " flagged" : ""}`}>
            <div className="brCell">
              <div className="brLabel">SOV Value</div>
              <div className="brVal sov">{item.sovValue}</div>
            </div>
            <div className="brDivider" />
            <div className="brCell">
              <div className="brLabel">Prev. Certified</div>
              <div className="brVal sov">{item.prevCertified}</div>
            </div>
            <div className="brDivider" />
            <div className="brCell">
              <div className="brLabel">This Draw</div>
              <div className={`brVal ${item.flagged ? "over" : "billed"}`}>
                {item.thisDraw}
              </div>
            </div>
            <div className="brDivider" />
            <div className="brCell">
              <div className="brLabel">GC Claims</div>
              <div className={`brVal ${item.flagged ? "over" : "ok"}`}>
                {item.claim}
              </div>
            </div>
            <Button
              className={`flagToggle${item.flagged ? " flagged" : ""}${pendingFlag ? " pending" : ""}`}
              onClick={handleFlagClick}
              aria-pressed={item.flagged}
            >
              {item.flagged ? "⚠ Flagged" : "Flag"}
            </Button>
          </div>
          <div className="statusBtns">
            {statusOptions.map((option) => {
              const selClass =
                activeStatus === option.value ? ` sel${option.value}` : "";
              return (
                <div
                  key={`${item.id}-${option.value}`}
                  className={`statusBtn${selClass}`}
                  onClick={() => onSetStatus(option.value)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={activeStatus === option.value}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      onSetStatus(option.value);
                  }}
                >
                  {option.title}
                  <br />
                  <span className="statusSubtitle">{option.subtitle}</span>
                </div>
              );
            })}
          </div>

          <div className="customPctWrap">
            <span className="customPctLabel">Override %:</span>
            <Input
              className={`customPctInput${item.customPct !== "" ? " overridden" : ""}`}
              type="number"
              min="0"
              max="100"
              placeholder="—"
              value={item.customPct}
              onChange={(e) => onSetCustomPct(e.target.value)}
              aria-label={`Custom percentage for ${item.desc}`}
            />
            <Button
              className="customPctClear"
              onClick={onClearCustomPct}
              aria-label="Clear custom percentage"
            >
              ✕
            </Button>
          </div>

          {hasComments && (
            <div className="committedComments">
              {flagComment && (
                <div className="committedComment flag">⚠ {flagComment}</div>
              )}
              {extraComments.map((c, i) => (
                <div className="committedComment extra" key={i}>
                  {c}
                </div>
              ))}
            </div>
          )}

          <TextArea
            ref={textareaRef}
            className="fiNotes"
            placeholder={`Field notes for ${item.desc}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            aria-label={`Notes for ${item.desc}`}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
        </div>
      </div>
    </div>
  );
}
